import clsx from 'clsx'
import { makeStyles, useTheme } from '@mui/styles'
import React, { ReactNode, useMemo } from 'react'

import lang from '@/lang'
import TenantInitialsBubble from '@/modules/my-account/tenant-information/tenant-initials-bubble'
import ThemeInterface from '@/theme-interface'
import { ColorModes } from '@/modules/layout/constants'

interface TenantSelectionItemBaseProps {
  tenantName: string,
  companyName?: string,
  styles?: { content: string },
  action?: ReactNode,
  tenantId?: string,
  isActive?: boolean,
  isDemo?: boolean,
  editModeEnabled?: boolean
  hideSeperator?: boolean,
  iconColors?: { backgroundColor: string, color: string },
  onClick?: (tenantId: string) => void
  onSubmitEdit?: ({ tenantName }: { tenantName: string }) => void
  onCancelEdit?: () => void
  customIcon?: ReactNode,
}

const useStyles = makeStyles((theme: ThemeInterface) => ({
  content: {
    display: 'grid',
    gridTemplateAreas: '"initials details action"',
    gridTemplateColumns: 'auto 1fr auto',
    padding: '15px 30px',
    cursor: 'pointer',
    '&:hover': {
      fontWeight: theme.fontWeights.medium,
      '& $action': {
        fontWeight: theme.fontWeights.medium,
      },
    },
  },
  seperator: {
    borderBottom: `1px solid ${theme.mode.borderColor.medium}`,
    opacity: theme.mode.type === ColorModes.dark ? 0.2 : 1.0,
    margin: '0px 30px',
  },
  initials: {
    gridArea: 'initials',
    paddingRight: 16,
    display: 'flex',
    alignItems: 'center',
  },
  initialsBubble: {
    width: 24,
    height: 24,
    border: `1px solid ${theme.borderColors.default}`,
    fontWeight: theme.fontWeights.regular,
  },
  details: {
    gridArea: 'details',
  },
  action: {
    gridArea: 'action',
    display: 'flex',
    alignItems: 'center',
  },
  customIcon: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: 24,
    height: 24,
    borderRadius: '50%',
    border: `1px solid ${theme.primary}`,
  },
}), { name: 'TenantSelectionItemBase' })

const TenantSelectionItemBase = (props: TenantSelectionItemBaseProps) => {
  const classes = useStyles()
  const theme: ThemeInterface = useTheme()
  const {
    isDemo,
    tenantId,
    hideSeperator,
    tenantName,
    companyName,
    action,
    styles,
    iconColors,
    onClick,
    customIcon,
  } = props
  const tenantInitialsStyles = useMemo(() => {
    return {
      root: classes.initialsBubble,
    }
  }, [classes])

  const contentClass = clsx(classes.content, styles?.content && styles.content)
  const iconCol = {
    color: iconColors?.color ?? theme.mode.color.default,
    backgroundColor: iconColors?.backgroundColor ?? 'transparent',
  }
  return (
    <>
      <div className={contentClass} onClick={() => onClick && onClick(tenantId)}>
        <div className={classes.initials}>
          {customIcon
            ? (
              <div className={classes.customIcon}>
                {customIcon}
              </div>
            )
            : (
              <TenantInitialsBubble
                isDemo={isDemo}
                styles={tenantInitialsStyles}
                iconColors={iconCol}
                tenantName={tenantName}
                companyName={companyName} />
            )}
        </div>
        <div className={classes.details}>
          <span>{tenantName || companyName || lang.tenant.newTenant}</span>
        </div>
        <div className={classes.action}>
          {action && action}
        </div>
      </div>
      {!hideSeperator && <div className={classes.seperator} />}
    </>
  )
}

export default TenantSelectionItemBase
